/* ═══════════════════════════════════════════════════════════════════
   WavePulseGauge — the single hero component for the Wave Pulse score.
   Drops into LiveWaveDashboard, WaveOrchestrationViz, HomePage, etc.

   Props:
     score    — 0-100 (required)
     size     — 'sm' | 'md' | 'lg' (default 'md')
     subtitle — optional small label below the score
     dense    — if true: drops the band hint text (tight contexts)

   The ring color, band label, and hover hint all come from
   `waveMetrics.js → WAVE_PULSE_BANDS`. Edit that file, never this one.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useState } from 'react';
import { Sparkles, Info } from 'lucide-react';
import { getPulseBand } from './waveMetrics';

const SIZE_PRESETS = {
  sm: { ring: 80,  stroke: 5, font: 'text-xl',   sub: 'text-[8px]',  band: 'text-[8px]'  },
  md: { ring: 140, stroke: 6, font: 'text-4xl',  sub: 'text-[10px]', band: 'text-[10px]' },
  lg: { ring: 200, stroke: 8, font: 'text-6xl',  sub: 'text-xs',     band: 'text-xs'     },
};

export default function WavePulseGauge({
  score = 0,
  size = 'md',
  subtitle,
  dense = false,
  onClick,
}) {
  const [hint, setHint] = useState(false);
  const band = getPulseBand(score);
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.md;
  const r = (preset.ring / 2) - preset.stroke - 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const dashOffset = c - (clamped / 100) * c;

  return (
    <div
      data-testid="wave-pulse-gauge"
      data-pulse-score={clamped}
      data-pulse-band={band.label}
      className={`relative inline-flex flex-col items-center ${onClick ? 'cursor-pointer' : ''}`}
      onMouseEnter={() => setHint(true)}
      onMouseLeave={() => setHint(false)}
      onFocus={() => setHint(true)}
      onBlur={() => setHint(false)}
      onClick={onClick}
      tabIndex={onClick ? 0 : -1}
    >
      <div className="relative" style={{ width: preset.ring, height: preset.ring }}>
        <svg
          className="-rotate-90"
          width={preset.ring}
          height={preset.ring}
          viewBox={`0 0 ${preset.ring} ${preset.ring}`}
        >
          <circle
            cx={preset.ring / 2} cy={preset.ring / 2} r={r}
            stroke="#2E3240" strokeWidth={preset.stroke} fill="none"
          />
          <circle
            cx={preset.ring / 2} cy={preset.ring / 2} r={r}
            stroke={band.color} strokeWidth={preset.stroke} fill="none"
            strokeDasharray={c}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 1.4s ease, stroke 220ms ease',
              filter: `drop-shadow(0 0 ${preset.stroke * 1.5}px ${band.glow})`,
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${preset.font} font-black leading-none tabular-nums`} style={{ color: band.color }}>
            {clamped}
          </span>
          <span className={`${preset.sub} text-gray-500 mt-0.5`}>/ 100</span>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        <Sparkles className="h-3 w-3" style={{ color: band.color }} />
        <span className={`${preset.band} font-black uppercase tracking-widest`} style={{ color: band.color }}>
          {band.label}
        </span>
        <Info className="h-2.5 w-2.5 text-gray-600" />
      </div>

      {subtitle && !dense && (
        <p className={`${preset.sub} text-gray-500 mt-0.5 leading-snug text-center max-w-[200px]`}>
          {subtitle}
        </p>
      )}

      {hint && (
        <div
          role="tooltip"
          className="absolute top-full mt-2 z-30 w-64 p-3 rounded-xl shadow-2xl pointer-events-none"
          style={{ background: '#141820', border: `1px solid ${band.color}55` }}
        >
          <p className="text-[11px] font-black text-white mb-1">Wave Pulse · {band.label}</p>
          <p className="text-[10px] text-gray-300 leading-snug mb-2">{band.hint}</p>
          <p className="text-[9px] text-gray-500 leading-snug italic">
            One number, 0–100. Combines post density, cross-platform spread, platform balance, and reply compression.
            Hover any metric card to learn more.
          </p>
        </div>
      )}
    </div>
  );
}
